import { OwnUpTextInput } from '@rategravity/own-up-component-library';
import { DollarIcon } from '@rategravity/own-up-component-library/icon-library/system-icons/standard-icons/dollar';
import { PercentIcon } from '@rategravity/own-up-component-library/icon-library/system-icons/standard-icons/percent';
import React, { useCallback } from 'react';

import { NumericFormat } from 'react-number-format';
import { LabelWithIcon, WidgetOptionsInput, WidgetTextInput } from '../../common';
import { loanTermsOptions } from '../../common/constants';
import { LoanTerms } from '../../common/types';
import { useDownPayment } from '../data/dataProvider';
import { DownPaymentDataActionsTypes, DownPaymentState } from '../types';

type BasicInputs = {
  downPayment: DownPaymentState['downPayment'];
  interestRate: DownPaymentState['interestRate'];
  loanTerm: DownPaymentState['loanTerm'];
};
type InputEvent =
  | {
      inputTitle: keyof Omit<BasicInputs, 'loanTerm'>;
      value: number;
    }
  | {
      inputTitle: 'loanTerm';
      value: LoanTerms;
    };

const actionMapping = {
  downPayment: DownPaymentDataActionsTypes.SET_DOWN_PAYMENT,
  interestRate: DownPaymentDataActionsTypes.SET_INTEREST_RATE,
  loanTerm: DownPaymentDataActionsTypes.SET_LOAN_TERM
} as const;

export const BasicInputsComponent = ({
  inputs,
  updateInputs,
  onClick
}: {
  inputs: BasicInputs;
  updateInputs: (inputs: InputEvent) => void;
  onClick: () => void;
}) => {
  const { downPayment, interestRate, loanTerm } = inputs;
  return (
    // eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions
    <div onClick={onClick}>
      <NumericFormat
        customInput={OwnUpTextInput}
        label={<LabelWithIcon label="Down payment amount" />}
        value={downPayment}
        labelPosition="outer"
        $leadingIcon={DollarIcon()}
        decimalScale={0}
        allowNegative={false}
        thousandSeparator
        onChange={(e) => {
          updateInputs({
            inputTitle: 'downPayment',
            value: Number(e.target.value.replace(/[^0-9.-]/g, ''))
          });
        }}
      />
      <WidgetTextInput
        label={<LabelWithIcon label="Interest rate" />}
        value={interestRate}
        onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
          updateInputs({
            inputTitle: 'interestRate',
            value: Number(e.target.value.replace(/[^0-9.-]/g, ''))
          })
        }
        $leadingIcon={<PercentIcon />}
        decimalScale={3}
        fixedDecimalScale={true}
      ></WidgetTextInput>
      <WidgetOptionsInput
        label={<LabelWithIcon label="Loan term" />}
        value={loanTerm}
        options={loanTermsOptions}
        onChange={(e: React.ChangeEvent<{ name?: string; value: unknown }>) =>
          updateInputs({
            inputTitle: 'loanTerm',
            value: e.target.value as LoanTerms
          })
        }
      />
    </div>
  );
};

export const BasicInputs = () => {
  const {
    state: { downPayment, interestRate, loanTerm },
    dispatch
  } = useDownPayment();
  const updateInputs = useCallback(
    (inputs: InputEvent) => {
      if (inputs.inputTitle === 'loanTerm') {
        const actionType = actionMapping[inputs.inputTitle];
        dispatch({ type: actionType, payload: inputs.value as LoanTerms });
      } else {
        const actionType = actionMapping[inputs.inputTitle];
        dispatch({ type: actionType, payload: inputs.value });
      }
    },
    [dispatch]
  );
  const openAdvancedOptions = useCallback(() => {
    dispatch({ type: DownPaymentDataActionsTypes.SET_SHOW_FULL, payload: true });
  }, [dispatch]);
  return (
    <BasicInputsComponent
      inputs={{ downPayment, interestRate, loanTerm }}
      updateInputs={updateInputs}
      onClick={openAdvancedOptions}
    />
  );
};
